import { useQuery } from '@tanstack/react-query'

import { ApiUrlPath, X_AUTH_TOKEN, IQuestion, Filter, AppMsg } from 'cat-shared/utils'
import { TEN_MINUTES_IN_MS } from 'client/utils'
import { fetch, getAbortController, getLocalAuthUser } from 'client/services'
import { usePageStateContext } from 'client/hooks'

export type TGetQuestionsData = {
  count: number
  data: IQuestion[]
}

type TError = {
  msg: AppMsg
}

type Args = {
  onSuccess?: (data: TGetQuestionsData) => void
  onError?: (err: TError) => void
}

type ReqParams = {
  pageNo: string
  filter: Filter
  category: string
  search: string
}

export default (args: Args) => {
  const { browseQuestionsPageState } = usePageStateContext()

  const getParams = (): ReqParams => {
    const { filter, category, search, pageNo } = browseQuestionsPageState
    const params = {} as ReqParams
    params.pageNo = String(pageNo)
    params.filter = filter
    if (category && category !== 'any') params.category = category
    if (search) params.search = search
    return params
  }

  const getQuestions = async () => {
    const token = getLocalAuthUser().token

    const abortController = getAbortController(25)

    const res = await fetch(
      ApiUrlPath.GetQuestions + '?' + new URLSearchParams(getParams()),
      {
        headers: { 'Content-Type': 'application/json', [X_AUTH_TOKEN]: token || '' },
        signal: abortController.signal,
      }
    )

    abortController.clear()

    if (res.ok) {
      const body = (await res.json()) as TGetQuestionsData
      return Promise.resolve(body)
    } else {
      const err = (await res.json()) as TError
      return Promise.reject(err)
    }
  }

  return useQuery<TGetQuestionsData, TError>([ApiUrlPath.GetQuestions], getQuestions, {
    enabled: false,
    keepPreviousData: true,
    cacheTime: 0,
    refetchInterval: TEN_MINUTES_IN_MS,
    refetchIntervalInBackground: true,
    onSuccess: args.onSuccess,
    onError: args.onError,
  })
}
